import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, Flex } from 'antd';
import Document from '../../pdf/Document';
import useApiClient from '../../../hooks/api/useApiClient';
import useFeedbackLabel from '../../../hooks/ui/useFeedbackLabel';
import { usePreviewServiceContractQuery } from '../../../store/api-slices';

/**
 * Step 3 — read-only PDF preview generated from the (unsaved) recipient details,
 * an Update button that refetches the preview, and the create-contract action
 * that commits the details via init-sign.
 *
 * `initSign` optionally overrides how signing is kicked off (same
 * `(id, data) => Promise<{ success, data }>` shape as
 * `ServiceContractsManager.initSign`); when omitted the manager's init-sign is used.
 */
export default function YousignPreviewStep({ contract, data, onBack, onCreated, initSign }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Contract:YousignContractFlow' });
  const { t: tSign } = useTranslation(undefined, { keyPrefix: 'Components:Contract:SignStep' });

  const { ServiceContractsManager } = useApiClient();

  const [signing, setSigning] = useState(false);
  // Every field posted to init-sign has a `form.<field>.label` key in the
  // sign-step namespace, so `FeedbackLabel` can name the rejected fields.
  const { setErrorFeedback, resetFeedback, renderFeedbackLabel } = useFeedbackLabel({
    t: tSign,
    className: 'mt-lg mb-none',
  });

  const {
    data: previewFile,
    isFetching: loadingPreview,
    refetch: refetchPreview,
  } = usePreviewServiceContractQuery(
    { apiManager: ServiceContractsManager, id: contract?.id, data },
    { refetchOnMountOrArgChange: true },
  );

  const handleSign = () => {
    resetFeedback();
    setSigning(true);
    toast.promise(
      new Promise((resolve, reject) => {
        (initSign ?? ServiceContractsManager.initSign)(contract?.id, data)
          .then((response) => {
            if (!response?.success || !response.data) {
              // On failure the Manager puts the backend body in `data` — surface
              // its validation errors instead of only the generic toast. A
              // bodyless failure leaves the alert empty, which is intended.
              setErrorFeedback(response?.data);

              return reject();
            }

            onCreated(response.data);

            return resolve();
          })
          .finally(() => setSigning(false));
      }),
      {
        pending: tSign('toasts.contract_signing_pending'),
        success: tSign('toasts.contract_got_signed'),
        error: tSign('toasts.failed_to_sign_contract'),
      },
    );
  };

  return (
    <div className="mt-medium">
      <Flex justify="center">
        <Document file={previewFile} loading={loadingPreview} />
      </Flex>
      {/* Below the preview rather than above it: the PDF is full-height, so an
          alert at the top of the step would sit a viewport away from the button
          that triggered it. */}
      {renderFeedbackLabel}
      <Flex justify="space-between" gap="small" className="mt-lg">
        <Button onClick={onBack}>{tSign('buttons.back')}</Button>
        <Flex justify="end" gap="small" className="ml-auto">
          <Button loading={loadingPreview} onClick={() => refetchPreview()}>
            {t('buttons.update')}
          </Button>
          <Button type="primary" loading={signing} onClick={handleSign}>
            {tSign('buttons.init_sign')}
          </Button>
        </Flex>
      </Flex>
    </div>
  );
}
